<script lang="ts">
  import { run } from "svelte/legacy";

  import { useQuery, useQueryClient } from "@sveltestack/svelte-query";
  import { Products } from "../../helpers/api";
  import type { Person, PersonValidation, TPinResult } from "../../helpers/types";
  import { personValidator } from "../../helpers/validators";
  import Card from "../atoms/Card.svelte";
  import ErrorText from "../atoms/ErrorText.svelte";
  import Heading from "../atoms/Heading.svelte";
  import NumberInput from "../atoms/NumberInput.svelte";
  import PhoneInput from "../atoms/PhoneInput.svelte";
  import TextInput from "../atoms/TextInput.svelte";
  import SocialSecurity from "../molecules/SocialSecurity.svelte";

  interface Props {
    person: Person;
    personValid?: boolean;
    validSocialSecurity?: boolean;
    disableInputs?: boolean;
    resetInvoice: () => void;
    hasPressedContinue?: boolean;
    fetchPersonAutomatic: () => void;
    hasFetchedPersonAutomatic: boolean;
    clearPersonInfo: () => void;
  }

  let {
    person = $bindable(),
    personValid = $bindable(),
    validSocialSecurity = $bindable(),
    disableInputs = false,
    resetInvoice,
    hasPressedContinue = $bindable(false),
    fetchPersonAutomatic,
    hasFetchedPersonAutomatic = $bindable(),
    clearPersonInfo,
  }: Props = $props();

  const client = useQueryClient();

  let personValidation: PersonValidation | undefined = $state();

  const validateInput = (person: Person) => {
    personValidation = personValidator(person);
    personValid = person.valid;
  };

  const updatePerson = (lookup: TPinResult) => {
    resetInvoice();
    const socialSecurity = person.socialSecurity;
    person = {};
    person.socialSecurity = socialSecurity;

    person = {
      firstname: lookup.firstname,
      lastname: lookup.lastname,
      city: lookup.city,
      zipcode: lookup.zipcode,
      address: lookup.address,
      ...person,
    };
  };

  const personQuery = useQuery(
    ["socialSecurity", person.socialSecurity],
    () => Products.pinLookup(person.socialSecurity),
    {
      enabled: false,
      onSuccess: (data) => {
        fetchPersonAutomatic();
        updatePerson(data.result);
      },
      cacheTime: 0,
    }
  );

  const onGetPerson = () => {
    $personQuery.remove();
    $personQuery.refetch();
  };

  $effect(() => {
    if (person) {
      validateInput(person);
    } else {
      person.valid = false;
      personValid = false;
    }
  });
</script>

<Card>
  <div class="text-M">
    <SocialSecurity
      {disableInputs}
      isLoading={$personQuery.isLoading}
      onChange={() => {
        if (!$personQuery.isIdle) client.cancelQueries();
      }}
      {onGetPerson}
      {hasPressedContinue}
      bind:socialSecurity={person.socialSecurity}
      bind:valid={validSocialSecurity}
      error={$personQuery.isError}
    />
    {#if hasFetchedPersonAutomatic}
      <button
        type="button"
        onclick={clearPersonInfo}
        class="text-Neutral900 text-sm leading-4 mb-4"
      >
        Stämmer inte uppgifterna nedan? <span class="underline hover:opacity-75"
          >Fyll i uppgifter manuellt</span
        >
      </button>
    {/if}
    <Heading variant={"secondary"}>Dina kontakt- och faktureringsuppgifter</Heading>
    <div class="flex mb-2 space-x-3 w-full">
      <div class="w-full">
        {#if (personValidation && personValidation.errors.firstname) || (!person.firstname && hasPressedContinue)}
          <ErrorText>Fyll i förnamn</ErrorText>
        {:else}
          <label for="firstname" class="block">Förnamn</label>
        {/if}
        <TextInput
          disabled={disableInputs || hasFetchedPersonAutomatic}
          autoFocus={false}
          autoFocusDelay={false}
          id="firstname"
          required={hasFetchedPersonAutomatic ? false : true}
          {hasPressedContinue}
          name="firstname"
          class="w-full"
          error={(personValidation && personValidation.errors.firstname) ||
            (!person.firstname && hasPressedContinue)}
          bind:value={person.firstname}
        />
      </div>
      <div class="w-full">
        {#if (personValidation && personValidation.errors.lastname) || (!person.lastname && hasPressedContinue)}
          <ErrorText>Fyll i efternamn</ErrorText>
        {:else}
          <label for="lastname" class="block">Efternamn</label>
        {/if}
        <TextInput
          disabled={disableInputs || hasFetchedPersonAutomatic}
          id="lastname"
          name="lastname"
          required={hasFetchedPersonAutomatic ? false : true}
          {hasPressedContinue}
          class="w-full"
          error={(personValidation && personValidation.errors.lastname) ||
            (!person.lastname && hasPressedContinue)}
          bind:value={person.lastname}
        />
      </div>
    </div>
    <div class="flex space-x-3 mb-6">
      <div class="w-full">
        {#if (personValidation && personValidation.errors.email) || (!person.email && hasPressedContinue)}
          <ErrorText>Fyll i giltig e-postadress</ErrorText>
        {:else}
          <label for="mail" class="block">E-postadress</label>
        {/if}
        <TextInput
          autoFocusDelay={false}
          {hasPressedContinue}
          id="mail"
          disabled={disableInputs}
          required={true}
          name="email"
          class="w-full"
          error={(personValidation && personValidation.errors.email) ||
            (!person.email && hasPressedContinue)}
          bind:value={person.email}
        />
      </div>
      <div class="w-full">
        {#if (personValidation && personValidation.errors.mobile) || (!person.mobile && hasPressedContinue)}
          <ErrorText>Fyll i mobilnummer</ErrorText>
        {:else}
          <label for="phone" class="block">Mobiltelefon</label>
        {/if}
        <PhoneInput
          disabled={disableInputs}
          error={(personValidation && personValidation.errors.mobile) ||
            (!person.mobile && hasPressedContinue)}
          bind:value={person.mobile}
          name={"phone"}
          {hasPressedContinue}
          required={true}
          class="w-full"
        />
      </div>
    </div>
    <div class="mb-4">
      {#if (personValidation && personValidation.errors.address) || (!person.address && hasPressedContinue)}
        <ErrorText>Fyll i gatuadress</ErrorText>
      {:else}
        <label for="adress" class="block">Gatuadress</label>
      {/if}
      <TextInput
        id="adress"
        disabled={disableInputs || hasFetchedPersonAutomatic}
        name="adress"
        {hasPressedContinue}
        required={hasFetchedPersonAutomatic ? false : true}
        class="w-full"
        error={(personValidation && personValidation.errors.address) ||
          (!person.address && hasPressedContinue)}
        bind:value={person.address}
      />
    </div>
    <div class="flex space-x-3">
      <div class=" ">
        <div class="flex-none">
          {#if (personValidation && personValidation.errors.zipcode) || (!person.zipcode && hasPressedContinue)}
            <ErrorText>Postnummer</ErrorText>
          {:else}
            <label for="zipcode" class="block">Postnummer</label>
          {/if}
        </div>
        {#if hasFetchedPersonAutomatic}
          <TextInput
            {hasPressedContinue}
            disabled={disableInputs || hasFetchedPersonAutomatic}
            id="zipcode"
            name="zipcode"
            required={hasFetchedPersonAutomatic ? false : true}
            class="w-28"
            error={(personValidation && personValidation.errors.zipcode) ||
              (!person.zipcode && hasPressedContinue)}
            bind:value={person.zipcode}
          />
        {:else}
          <NumberInput
            {hasPressedContinue}
            disabled={disableInputs || hasFetchedPersonAutomatic}
            id="zipcode"
            name="zipcode"
            required={hasFetchedPersonAutomatic ? false : true}
            class="w-28"
            error={(personValidation && personValidation.errors.zipcode) ||
              (!person.zipcode && hasPressedContinue)}
            bind:value={person.zipcode}
          />
        {/if}
      </div>
      <div class="w-full">
        {#if (personValidation && personValidation.errors.city) || (!person.city && hasPressedContinue)}
          <ErrorText>Fyll i postort</ErrorText>
        {:else}
          <label for="city" class="block">Postort</label>
        {/if}
        <TextInput
          id="city"
          name="city"
          required={hasFetchedPersonAutomatic ? false : true}
          {hasPressedContinue}
          disabled={disableInputs || hasFetchedPersonAutomatic}
          class="w-full"
          error={(personValidation && personValidation.errors.city) ||
            (!person.city && hasPressedContinue)}
          bind:value={person.city}
        />
      </div>
    </div>
  </div>
</Card>
